import { freqToMidi } from '@muzly/voice-engine';

/**
 * probeLatch — 계측 오버레이(`CaptureProbe`)의 두 칸을 테이크 동안 모은다.
 *
 * TestFlight 2차 #67은 두 가지를 말했다: "'아'가 바로 집음되지 않는다"와 "라를 안내하는데
 * C3 부근으로 표시된다". 실기기에서만 나는 증상이라(`.claude/20260910/DEBUG-15-34-53.md`
 * status cannot-reproduce) 한 테이크로 가를 값을 화면에 올린다:
 *
 * - `onset` — 게이트의 첫 채택으로 이어진 엔진 유성 구간의 시작부터 그 채택까지(ms). 사용자가 언제 부르기 시작했는지와 무관하게 **게이트가 붙잡고 있던 시간**만 남는다.
 *   바닥을 재는 300ms(`CALIBRATION_MS`) 안에 부르기 시작하면 그만큼은 늘 보인다.
 * - `oct` — 채점이 쓰는 raw 음과 화면이 그리는 smoothed 음이 옥타브만큼 갈린 프레임 수.
 *   raw에는 네이티브의 옥타브 억제가 안 걸린다(설계상 두 계열 분리). 대칭으로 세므로 수만으로
 *   어느 쪽이 틀렸는지는 모른다 — smoothed는 진짜 옥타브 도약도 잠시 옛 옥타브에 붙잡아 둔다
 *   (`post_processor.hpp`). 어느 쪽인지는 화면 음과 대조해 읽는다.
 *
 * 프레임마다 한 번 도는 O(1) 갱신이라 오버레이가 꺼져 있어도 비용이 없다. React·시간 의존
 * 없음 — vitest 대상.
 */

export interface ProbeLatch {
  /** 게이트의 첫 채택으로 이어진 엔진 유성 구간(게이트 앞)이 시작된 시각. */
  firstEngineVoiceAtMs: number | null;
  /** 게이트가 처음 목소리로 받아들인 프레임의 시각. */
  firstVoiceAtMs: number | null;
  /** 게이트가 받아들인 프레임 중 raw와 smoothed가 옥타브로 갈린 수. */
  octaveMismatch: number;
}

export interface ProbeStep {
  tMs: number;
  /** 엔진의 유성 판정 — 게이트를 거치기 **전**. */
  engineVoiced: boolean;
  /** 게이트를 거친 최종 판정. */
  isVoice: boolean;
  /** 엔진의 raw f0 — 채점이 쓰는 값. */
  f0Hz: number | null;
  /** 엔진의 표시용 f0 — 화면이 그리는 값. */
  smoothedF0Hz: number | null;
}

export const INITIAL_PROBE_LATCH: ProbeLatch = {
  firstEngineVoiceAtMs: null,
  firstVoiceAtMs: null,
  octaveMismatch: 0,
};

/**
 * 옥타브로 갈렸다고 볼 거리(반음). 12가 한 옥타브인데 10으로 둔다 — 옥타브 오검출은
 * 비브라토·스무딩 지연만큼 12에서 조금 모자라게 찍히고, 반옥타브(6) 근처의 진짜 음 이동과는
 * 여전히 떨어져 있다.
 */
const OCTAVE_APART_SEMITONES = 10;

const octaveApart = (
  f0Hz: number | null,
  smoothedF0Hz: number | null,
): boolean =>
  f0Hz != null &&
  smoothedF0Hz != null &&
  f0Hz > 0 &&
  smoothedF0Hz > 0 &&
  Math.abs(freqToMidi(f0Hz) - freqToMidi(smoothedF0Hz)) >=
    OCTAVE_APART_SEMITONES;

export const stepProbeLatch = (
  prev: ProbeLatch,
  step: ProbeStep,
): ProbeLatch => {
  // 기준점은 **첫 채택으로 이어진** 엔진 유성 구간의 시작이다. 채택 전에 엔진 유성이 끊기면
  // (음정 있는 방 소음 한 프레임, 확신 미달) 기준점을 버린다 — 그 뒤 조용히 있던 시간은
  // 게이트가 붙잡은 시간이 아니다. 채택 뒤로는 고정된다. 한계: 발성 초입에 엔진 유성이
  // 한 프레임만 끊겨도 버리므로 onset은 작게 나올 수 있다(과소 보고 쪽 — 붉게 뜨면 확실하다).
  const firstEngineVoiceAtMs =
    prev.firstVoiceAtMs != null
      ? prev.firstEngineVoiceAtMs
      : step.engineVoiced
        ? (prev.firstEngineVoiceAtMs ?? step.tMs)
        : null;
  const firstVoiceAtMs =
    prev.firstVoiceAtMs ?? (step.isVoice ? step.tMs : null);
  const mismatch =
    step.isVoice && octaveApart(step.f0Hz, step.smoothedF0Hz) ? 1 : 0;
  if (
    firstEngineVoiceAtMs === prev.firstEngineVoiceAtMs &&
    firstVoiceAtMs === prev.firstVoiceAtMs &&
    mismatch === 0
  ) {
    return prev;
  }
  return {
    firstEngineVoiceAtMs,
    firstVoiceAtMs,
    octaveMismatch: prev.octaveMismatch + mismatch,
  };
};

/** 엔진이 처음 들은 때 → 게이트가 처음 받아들인 때(ms). 둘 중 하나라도 아직 없으면 null. */
export const onsetMs = (latch: ProbeLatch): number | null =>
  latch.firstEngineVoiceAtMs == null || latch.firstVoiceAtMs == null
    ? null
    : latch.firstVoiceAtMs - latch.firstEngineVoiceAtMs;
